import { type ReactNode, useRef } from 'react';
import { UnifiedPanelsSVG } from '../UnifiedPanelsSVG';
import { BrandingPill } from '../BrandingPill';
import './vortex-layout.css';

interface VortexLayoutProps {
  /** Profile vortex component */
  vortex: ReactNode;
  /** Content for the left panel (notes) - full component with header */
  notesContent: ReactNode;
  /** Content for the right panel (tasks/gauge) - full component with header */
  tasksContent: ReactNode;
}

/**
 * VortexLayout Component
 *
 * A unified layout where all content panels flow around three floating islands:
 * 1. Branding pill at the top
 * 2. Profile vortex in the middle
 * 3. Dock at the bottom (rendered separately via DockProvider)
 *
 * The negative space creates a continuous channel connecting all elements.
 */
export function VortexLayout({
  vortex,
  notesContent,
  tasksContent,
}: VortexLayoutProps): JSX.Element {
  const vortexRef = useRef<HTMLDivElement>(null);

  return (
    <div className="vortex-layout">
      {/* Unified SVG drawing all panel shapes */}
      <UnifiedPanelsSVG />

      {/* Branding pill floating in top negative space */}
      <BrandingPill />

      {/* Profile vortex floating in middle negative space */}
      <div className="vortex-layout__vortex" ref={vortexRef}>
        {vortex}
      </div>

      {/* Content panel overlays - panels clip their content via overflow: hidden */}
      <div className="vortex-layout__panel vortex-layout__panel--notes">
        {notesContent}
      </div>
      <div className="vortex-layout__panel vortex-layout__panel--tasks">
        {tasksContent}
      </div>
    </div>
  );
}

export default VortexLayout;
